import React, { useEffect, useState } from 'react';
import { api, DonationCenterDto, DonationCenterGeo } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';

// ── Donation Centers — admin-side management ────────────────────────────────
// A tenant-curated directory a customer picks from at booking time for any service type
// flagged PricingRule.RequiresDonationCenter (see the checkbox in the Pricing panel).

const fieldLabelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' };

export function DonationCentersPanel() {
    const [centers, setCenters] = useState<DonationCenterDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<DonationCenterDto | 'new' | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getDonationCentersAdmin().then(setCenters).catch(e => setError(e.message || 'Failed to load donation centers.')).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    if (editing) {
        return <DonationCenterForm center={editing === 'new' ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
    }

    const remove = async (c: DonationCenterDto) => {
        if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
        try { await api.deleteDonationCenter(c.id); load(); }
        catch (e: any) { setError(e.message || 'Failed to delete.'); }
    };

    return (
        <div style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Donation Centers</h2>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Customers pick from this list when booking a Donation Run-type service</div>
                </div>
                <Btn onClick={() => setEditing('new')}>+ New center</Btn>
            </div>

            <Alert message={error} />

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {centers.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No donation centers yet.</div>}
                    {centers.map((c, i) => (
                        <div key={c.id} style={{
                            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                            borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                        }}>
                            <span style={{
                                fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                background: c.isActive ? C.greenBg : '#F5F5F5',
                                color: c.isActive ? C.greenMid : C.textMuted,
                                flexShrink: 0, minWidth: 60, textAlign: 'center',
                            }}>{c.isActive ? 'Active' : 'Inactive'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.name}</div>
                                <div style={{ fontSize: 12, color: C.textSub }}>{c.address}</div>
                            </div>
                            <Btn variant="outline" onClick={() => setEditing(c)}>Edit</Btn>
                            <Btn variant="danger" onClick={() => remove(c)}>Delete</Btn>
                        </div>
                    ))}
                </Card>
            )}
        </div>
    );
}

function DonationCenterForm({ center, onCancel, onSaved }: { center: DonationCenterDto | null; onCancel: () => void; onSaved: () => void }) {
    const [name, setName] = useState(center?.name ?? '');
    const [address, setAddress] = useState(center?.address ?? '');
    const [lat, setLat] = useState<number | undefined>(center?.lat);
    const [lng, setLng] = useState<number | undefined>(center?.lng);
    const [city, setCity] = useState(center?.city);
    const [state, setStateVal] = useState(center?.state);
    const [county, setCounty] = useState(center?.county);
    const [postalCode, setPostalCode] = useState(center?.postalCode);
    const [country, setCountry] = useState(center?.country);
    const [phone, setPhone] = useState(center?.phone ?? '');
    const [taxId, setTaxId] = useState(center?.taxId ?? '');
    const [description, setDescription] = useState(center?.description ?? '');
    const [isActive, setIsActive] = useState(center?.isActive ?? true);
    const [sortOrder, setSortOrder] = useState(String(center?.sortOrder ?? 0));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        if (!name.trim() || !address.trim()) { setError('Name and address are required.'); return; }
        setSaving(true); setError('');
        try {
            const sort = parseInt(sortOrder) || 0;
            const geo: DonationCenterGeo = { lat, lng, city, state, county, postalCode, country };
            if (center) {
                await api.updateDonationCenter(center.id, name.trim(), address.trim(), geo, phone.trim() || undefined, taxId.trim() || undefined, isActive, sort, description.trim() || undefined);
            } else {
                await api.addDonationCenter(name.trim(), address.trim(), geo, phone.trim() || undefined, taxId.trim() || undefined, sort, description.trim() || undefined);
            }
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 480, padding: 28 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: C.text }}>{center ? 'Edit donation center' : 'New donation center'}</h2>
            <Alert message={error} />

            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Goodwill — Main St" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <AddressAutocomplete
                    label="Address"
                    value={address}
                    onChange={r => {
                        setAddress(r.address); setLat(r.lat); setLng(r.lng);
                        setCity(r.city); setStateVal(r.state); setCounty(r.county); setPostalCode(r.postalCode); setCountry(r.country);
                    }}
                    placeholder="123 Main St, Anytown, ST 00000"
                    required
                />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Phone (optional)</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Tax ID / EIN (optional)</label>
                <input value={taxId} onChange={e => setTaxId(e.target.value)} placeholder="Printed on the customer's donation receipt" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Description (optional)</label>
                <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="What this center accepts and who it benefits — shown to customers when they tap this center at booking"
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                    <label style={fieldLabelStyle}>Sort order</label>
                    <input value={sortOrder} onChange={e => setSortOrder(e.target.value)} type="number" style={inputStyle} />
                </div>
                {center && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 22 }}>
                        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} style={{ accentColor: C.greenMid, width: 16, height: 16 }} />
                        <span style={{ fontSize: 13, color: C.text }}>Active</span>
                    </label>
                )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
                <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
            </div>
        </Card>
    );
}
